
// Refactored for readability/maintainability and with comments for complex logic.
// See custom hook in useSelectTimeState.ts for state and navigation logic.

import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { AlertCircle, X } from "lucide-react";
import SelectTimeContainer from "@features/booking/components/SelectTimeContainer";
import BookingProgressFooter from "@features/booking/components/BookingProgressFooter";
import SelectTimeMain from "@features/booking/components/SelectTimeMain";
import ErrorBoundary from "@shared/components/common/ErrorBoundary";
import { useSelectTimeState } from "@features/booking";
import { useBookingFlow } from "@features/booking/flow";
import { useAnalytics, ANALYTICS_EVENTS } from "@core/analytics";
import { useRefreshAuthProfile } from "@core/auth";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogAction,
} from "@shared/components/ui/alert-dialog";
import type { LimitWarningType } from "@features/booking/hooks/useBookingFlow";

/**
 * SelectTime Page (Stepper)
 * Handles the entire "Pick a time" flow.
 * - All business logic is isolated in the `useSelectTimeState` hook.
 * - This component is now a lightweight orchestrator.
 */
const SelectTime = () => {
  const { progress } = useBookingFlow();
  const { t } = useTranslation('booking');
  const { trackEvent } = useAnalytics();
  useRefreshAuthProfile();

  useEffect(() => {
    trackEvent({ name: ANALYTICS_EVENTS.BOOKING_SELECT_TIME });
  }, [trackEvent]);

  // Get all state, handlers, and computed values from custom hook
  const {
    showExperience,
    selectedExp,
    setSelectedExp,
    selectedTime,
    loading,
    EXPERIENCES,
    availableTimes,
    handleSelectTime,
    handleBack,
    getSelectedExperienceLabel,
    handleJoinWaitingList,
    columnType,
    allowWaitingList,
    isWalkInDay,
    isWaitingListDay,
    centerHasOnlyPreferred,
    isAuthenticated,
    handlePreferredLogin,
    limitWarning,
    closeLimitWarning,
    tipPolicyRefreshNotice,
    dismissTipPolicyRefreshNotice,
    crossServiceAlternatives,
    handleSelectCrossServiceSlot,
    calendarAvailability,
    isCalendarLoading,
    handleCalendarDateSelect,
    showNoAvailabilityCalendar,
    calendarServiceTypes,
    currentService,
    partnerSuggestions,
    alternativeMenus,
    handleSelectAlternativeMenu,
  } = useSelectTimeState();

  return (
    <SelectTimeContainer>
      {/* Carry-over notice: a commit attempt on ANOTHER page (Personalize,
          Guarantee/payment, or Confirmation) was rejected because the tip
          options the guest had committed against went stale — see
          useReservationCommit's TIP_STALE_CODES branch. That hook's local
          `error` state doesn't survive the redirect (the commit-host page
          unmounts in the same tick), so the copy is carried here via
          `booking.tipPolicyRefreshMessage` and read once on mount. Same
          role="alert" + icon shape as StripePaymentForm/MercadoPagoPaymentForm's
          `declineNotice` for the same kind of cross-page carry-over; this one
          also gets an explicit dismiss since there's no "next submit" here to
          retire it implicitly. */}
      {tipPolicyRefreshNotice && (
        <div
          role="alert"
          data-testid="tip-policy-refresh-notice"
          className="flex items-center justify-between gap-3 rounded-xl border border-destructive/30 bg-destructive/10 px-4 py-3 mb-4 text-sm text-destructive"
        >
          <div className="flex items-center gap-3">
            <AlertCircle size={18} className="shrink-0" />
            <span>{tipPolicyRefreshNotice}</span>
          </div>
          <button
            type="button"
            onClick={dismissTipPolicyRefreshNotice}
            aria-label={t('LIMIT_WARNING_CLOSE')}
            className="-m-3.5 shrink-0 rounded-md p-3.5 text-destructive hover:opacity-70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          >
            <X size={16} />
          </button>
        </div>
      )}
      <ErrorBoundary>
        <SelectTimeMain
          showExperience={showExperience}
          EXPERIENCES={EXPERIENCES}
          selectedExp={selectedExp}
          onExperienceChange={setSelectedExp}
          getSelectedExperienceLabel={getSelectedExperienceLabel}
          availableTimes={availableTimes}
          selectedTime={selectedTime}
          handleSelectTime={handleSelectTime}
          loading={loading}
          onJoinWaitingList={handleJoinWaitingList}
          columnType={columnType}
          allowWaitingList={allowWaitingList}
          isWalkInDay={isWalkInDay}
          isWaitingListDay={isWaitingListDay}
          centerHasOnlyPreferred={centerHasOnlyPreferred}
          isAuthenticated={isAuthenticated}
          onPreferredLogin={handlePreferredLogin}
          crossServiceAlternatives={crossServiceAlternatives}
          onSelectCrossServiceSlot={handleSelectCrossServiceSlot}
          calendarAvailability={calendarAvailability}
          isCalendarLoading={isCalendarLoading}
          onCalendarDateSelect={handleCalendarDateSelect}
          showNoAvailabilityCalendar={showNoAvailabilityCalendar}
          calendarServiceTypes={calendarServiceTypes}
          currentService={currentService}
          partnerSuggestions={partnerSuggestions}
          alternativeMenus={alternativeMenus}
          onSelectAlternativeMenu={handleSelectAlternativeMenu}
        />
      </ErrorBoundary>
      <BookingProgressFooter onBack={handleBack} progress={progress} />

      <LimitWarningDialog
        warning={limitWarning}
        onClose={closeLimitWarning}
        t={t}
      />
    </SelectTimeContainer>
  );
};

function LimitWarningDialog({
  warning,
  onClose,
  t,
}: {
  warning: { open: boolean; type: LimitWarningType; limit: number; contactEmail: string } | null;
  onClose: () => void;
  t: (key: string, opts?: Record<string, unknown>) => string;
}) {
  if (!warning) return null;

  const isBlocked = warning.type === 'blocked';
  const title = isBlocked
    ? t('BLOCKED_USER_TITLE')
    : t('MODAL_WARNING_DAILY_TITLE_1');

  let subtitle = '';
  let text = '';
  if (isBlocked) {
    text = t('BLOCKED_USER_SUBTITLE');
  } else {
    subtitle = t(`MODAL_WARNING_${warning.type.toUpperCase()}_TITLE_2`, { bookings: warning.limit });
    text = t(`MODAL_WARNING_${warning.type.toUpperCase()}_TEXT_1`, { bookings: warning.limit });
  }

  return (
    <AlertDialog open={warning.open} onOpenChange={(open) => { if (!open) onClose(); }}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-2">
              {subtitle && <p className="font-medium text-foreground">{subtitle}</p>}
              <p>{text}</p>
              {!isBlocked && warning.contactEmail && (
                <a
                  href={`mailto:${warning.contactEmail}`}
                  className="block text-sm font-medium text-primary underline"
                >
                  {warning.contactEmail}
                </a>
              )}
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogAction onClick={onClose}>
            {t('LIMIT_WARNING_CLOSE')}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export default SelectTime;
